import {
  createContext,
  useContext,
  useState,
  useMemo,
  type ReactNode,
} from "react";

interface ExpenseFormState {
  description: string;
  amount: number;
  paidById: string;
  participantIds: string[];
}

interface ExpenseFormContextValue extends ExpenseFormState {
  setDescription: (value: string) => void;
  setAmount: (value: number) => void;
  setPaidById: (value: string) => void;
  setParticipantIds: (ids: string[]) => void;
  isInitialized: boolean;
}

const ExpenseFormContext = createContext<ExpenseFormContextValue | null>(null);

interface ExpenseFormProviderProps {
  children: ReactNode;
  initialValues?: ExpenseFormState;
}

export function ExpenseFormProvider({
  children,
  initialValues,
}: ExpenseFormProviderProps) {
  const [description, setDescription] = useState(
    initialValues?.description ?? ""
  );
  const [amount, setAmount] = useState(initialValues?.amount ?? 0);
  const [paidById, setPaidById] = useState(initialValues?.paidById ?? "");
  const [participantIds, setParticipantIds] = useState<string[]>(
    initialValues?.participantIds ?? []
  );

  // Mark as initialized when there are any meaningful values.
  // On a fresh refresh in step-2, the provider mounts fresh and these are all empty,
  // so step-2 page knows to redirect back to step-1.
  const isInitialized =
    initialValues !== undefined ||
    description.trim().length > 0 ||
    amount > 0 ||
    participantIds.length > 0;

  const value = useMemo<ExpenseFormContextValue>(
    () => ({
      description,
      amount,
      paidById,
      participantIds,
      setDescription,
      setAmount,
      setPaidById,
      setParticipantIds,
      isInitialized,
    }),
    [description, amount, paidById, participantIds, isInitialized]
  );

  return (
    <ExpenseFormContext.Provider value={value}>
      {children}
    </ExpenseFormContext.Provider>
  );
}

export function useExpenseForm() {
  const ctx = useContext(ExpenseFormContext);
  if (!ctx) {
    throw new Error(
      "useExpenseForm must be used within an ExpenseFormProvider"
    );
  }
  return ctx;
}
